import { authenticatedFetch } from '../auth-session';
import { parseFailure, publishWarnings } from '../response-feedback';
import { getTokens } from '../auth-storage';
import { MasterdataError } from '../masterdata/repository';

function authorization(): { Authorization: string } {
  const token = getTokens()?.accessToken;
  if (!token)
    throw new MasterdataError(
      'Your session has ended. Sign in again to continue.',
      401,
      'UNAUTHORIZED'
    );
  return { Authorization: `Bearer ${token}` };
}

async function fileError(response: Response): Promise<never> {
  const body: unknown = await response.json().catch(() => null);
  const failure = parseFailure(body, response.status);
  throw new MasterdataError(
    failure.message,
    response.status,
    failure.code,
    failure.fields,
    failure.payload
  );
}

export function downloadFilename(disposition: string | null, fallback: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '');
  const quoted = /filename="([^"]+)"/i.exec(disposition ?? '');
  let filename = quoted?.[1] ?? fallback;
  if (encoded) {
    try {
      filename = decodeURIComponent(encoded[1]);
    } catch {
      /* Fall back to the plain name. */
    }
  }
  return (
    [...filename]
      .map((character) =>
        character.charCodeAt(0) < 32 ||
        character.charCodeAt(0) === 127 ||
        character === '/' ||
        character === '\\'
          ? '_'
          : character
      )
      .join('') || 'download'
  );
}

export async function downloadOriginal(path: string, fallback: string): Promise<void> {
  const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    headers: authorization(),
  });
  if (!response.ok) await fileError(response);
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = downloadFilename(response.headers.get('Content-Disposition'), fallback);
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    // Allow the browser to start reading the object URL before releasing it.
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  }
}

export async function readFileBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    headers: authorization(),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) await fileError(response);
  return response.blob();
}

export async function uploadWorkbook(
  path: string,
  file: File,
  fields: Record<string, string> = {}
): Promise<unknown> {
  const body = new FormData();
  body.append('file', file);
  for (const [key, value] of Object.entries(fields)) {
    if (value !== null && value !== undefined && value !== '') body.append(key, value);
  }
  // Retry only an authentication refusal; a lost response may still have committed a preview.
  const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    method: 'POST',
    headers: authorization(),
    body,
  });
  if (!response.ok) await fileError(response);
  const result: unknown = await response.json();
  publishWarnings(result);
  return result;
}
